import type { FC } from "react"

import type { LandingPageBlock } from "@forento/core/models/landing"

type SvgProps = { className?: string }

const HeaderSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Logo */}
		<rect x="6" y="8" width="14" height="6" rx="1.5" fill="currentColor" fillOpacity="0.5" />
		{/* Nav links */}
		<rect x="62" y="9.5" width="10" height="3" rx="1" fill="currentColor" fillOpacity="0.25" />
		<rect x="76" y="9.5" width="10" height="3" rx="1" fill="currentColor" fillOpacity="0.25" />
		<rect x="90" y="7.5" width="24" height="7" rx="3.5" fill="currentColor" fillOpacity="0.45" />
		{/* Divider */}
		<line x1="0" y1="22" x2="120" y2="22" stroke="currentColor" strokeOpacity="0.12" strokeWidth="1" />
		{/* Hero content below header */}
		<rect x="20" y="29" width="80" height="6" rx="2" fill="currentColor" fillOpacity="0.2" />
		<rect x="30" y="39" width="60" height="4" rx="1.5" fill="currentColor" fillOpacity="0.12" />
		<rect x="43" y="47" width="34" height="6" rx="3" fill="currentColor" fillOpacity="0.3" />
	</svg>
)

const HeroSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Large title */}
		<rect x="14" y="8" width="92" height="8" rx="2" fill="currentColor" fillOpacity="0.4" />
		{/* Subtitle line 1 */}
		<rect x="24" y="21" width="72" height="4" rx="1.5" fill="currentColor" fillOpacity="0.2" />
		{/* Subtitle line 2 */}
		<rect x="30" y="28" width="60" height="4" rx="1.5" fill="currentColor" fillOpacity="0.2" />
		{/* CTA button */}
		<rect x="40" y="38" width="40" height="11" rx="5.5" fill="currentColor" fillOpacity="0.45" />
	</svg>
)

const TextSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		<rect x="8" y="7" width="104" height="5" rx="1.5" fill="currentColor" fillOpacity="0.35" />
		<rect x="8" y="16" width="104" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="8" y="24" width="104" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="8" y="32" width="85" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="8" y="40" width="96" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="8" y="48" width="68" height="4" rx="1" fill="currentColor" fillOpacity="0.2" />
	</svg>
)

const TwoColumnsSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Image placeholder (left) */}
		<rect x="6" y="7" width="50" height="42" rx="2" fill="currentColor" fillOpacity="0.14" />
		<line x1="6" y1="7" x2="56" y2="49" stroke="currentColor" strokeOpacity="0.1" strokeWidth="1" />
		<line x1="56" y1="7" x2="6" y2="49" stroke="currentColor" strokeOpacity="0.1" strokeWidth="1" />
		{/* Title */}
		<rect x="64" y="11" width="50" height="5.5" rx="1.5" fill="currentColor" fillOpacity="0.4" />
		{/* Text lines */}
		<rect x="64" y="21" width="50" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="64" y="28" width="44" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="64" y="35" width="48" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		{/* Button */}
		<rect x="64" y="43" width="30" height="8" rx="4" fill="currentColor" fillOpacity="0.4" />
	</svg>
)

const FeaturesSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Section title */}
		<rect x="30" y="5" width="60" height="5.5" rx="1.5" fill="currentColor" fillOpacity="0.35" />
		{/* 3 feature cards */}
		{([0, 1, 2] as const).map(i => (
			<g key={i} transform={`translate(${5 + i * 38}, 17)`}>
				<rect width="33" height="32" rx="2.5" fill="currentColor" fillOpacity="0.08" />
				{/* Icon */}
				<rect x="11" y="4" width="11" height="9" rx="2" fill="currentColor" fillOpacity="0.35" />
				{/* Title */}
				<rect x="4" y="18" width="25" height="3.5" rx="1" fill="currentColor" fillOpacity="0.3" />
				{/* Description */}
				<rect x="4" y="25" width="25" height="2.5" rx="1" fill="currentColor" fillOpacity="0.15" />
			</g>
		))}
	</svg>
)

const TestimonialsSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		<rect x="28" y="5" width="64" height="5.5" rx="1.5" fill="currentColor" fillOpacity="0.35" />
		{([0, 1, 2] as const).map(i => (
			<g key={i} transform={`translate(${5 + i * 38}, 17)`}>
				<rect width="33" height="32" rx="2.5" fill="currentColor" fillOpacity="0.08" />
				<rect x="4" y="5" width="20" height="2.5" rx="1" fill="currentColor" fillOpacity="0.35" />
				<rect x="4" y="11" width="25" height="2.5" rx="1" fill="currentColor" fillOpacity="0.18" />
				<rect x="4" y="16" width="22" height="2.5" rx="1" fill="currentColor" fillOpacity="0.18" />
				<circle cx="9" cy="26" r="4" fill="currentColor" fillOpacity="0.35" />
				<rect x="16" y="23" width="13" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
				<rect x="16" y="28" width="9" height="2" rx="1" fill="currentColor" fillOpacity="0.16" />
			</g>
		))}
	</svg>
)

const FaqSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Section title */}
		<rect x="28" y="5" width="64" height="5.5" rx="1.5" fill="currentColor" fillOpacity="0.35" />
		{/* Accordion rows */}
		{([0, 1, 2] as const).map(i => (
			<g key={i} transform={`translate(12, ${16 + i * 12})`}>
				<rect width="96" height="9" rx="2" fill="currentColor" fillOpacity="0.08" />
				<rect x="6" y="3" width={i === 1 ? 52 : 62} height="3" rx="1" fill="currentColor" fillOpacity="0.28" />
				<rect x="84" y="3" width="6" height="3" rx="1" fill="currentColor" fillOpacity="0.38" />
			</g>
		))}
	</svg>
)

const GallerySvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{([0, 1, 2] as const).map(col =>
			([0, 1] as const).map(row => (
				<g key={`${col}-${row}`} transform={`translate(${5 + col * 38}, ${3 + row * 27})`}>
					<rect width="32" height="24" rx="2" fill="currentColor" fillOpacity="0.15" />
					<line x1="0" y1="0" x2="32" y2="24" stroke="currentColor" strokeOpacity="0.09" strokeWidth="1" />
					<line x1="32" y1="0" x2="0" y2="24" stroke="currentColor" strokeOpacity="0.09" strokeWidth="1" />
				</g>
			)),
		)}
	</svg>
)

const LeadFormSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Title */}
		<rect x="22" y="7" width="76" height="5.5" rx="1.5" fill="currentColor" fillOpacity="0.35" />
		{/* Subtitle */}
		<rect x="30" y="16" width="60" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		{/* Input field */}
		<rect
			x="14"
			y="25"
			width="92"
			height="11"
			rx="2.5"
			stroke="currentColor"
			strokeOpacity="0.35"
			strokeWidth="1.5"
		/>
		{/* Placeholder text in input */}
		<rect x="20" y="29" width="40" height="3" rx="1" fill="currentColor" fillOpacity="0.15" />
		{/* Submit button */}
		<rect x="34" y="42" width="52" height="10" rx="5" fill="currentColor" fillOpacity="0.45" />
	</svg>
)

const ProductListSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{([0, 1, 2] as const).map(i => (
			<g key={i} transform={`translate(${5 + i * 38}, 7)`}>
				<rect width="33" height="42" rx="2.5" fill="currentColor" fillOpacity="0.08" />
				{/* Product image */}
				<rect x="2" y="2" width="29" height="20" rx="1.5" fill="currentColor" fillOpacity="0.2" />
				<line x1="2" y1="2" x2="31" y2="22" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
				<line x1="31" y1="2" x2="2" y2="22" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
				{/* Title */}
				<rect x="3" y="26" width="22" height="3.5" rx="1" fill="currentColor" fillOpacity="0.3" />
				{/* Price */}
				<rect x="3" y="33" width="14" height="3" rx="1" fill="currentColor" fillOpacity="0.45" />
				{/* Button */}
				<rect x="3" y="38" width="27" height="2.5" rx="1" fill="currentColor" fillOpacity="0.15" />
			</g>
		))}
	</svg>
)

const ProductHighlightSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Large image */}
		<rect x="6" y="6" width="50" height="44" rx="2" fill="currentColor" fillOpacity="0.15" />
		<line x1="6" y1="6" x2="56" y2="50" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
		<line x1="56" y1="6" x2="6" y2="50" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
		{/* Title */}
		<rect x="64" y="9" width="50" height="6.5" rx="2" fill="currentColor" fillOpacity="0.4" />
		{/* Description lines */}
		<rect x="64" y="20" width="50" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="64" y="27" width="42" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		<rect x="64" y="34" width="46" height="3.5" rx="1" fill="currentColor" fillOpacity="0.2" />
		{/* Price */}
		<rect x="64" y="41" width="20" height="4" rx="1.5" fill="currentColor" fillOpacity="0.45" />
		{/* CTA */}
		<rect x="64" y="48" width="50" height="6" rx="3" fill="currentColor" fillOpacity="0.4" />
	</svg>
)

const BlogPostsSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Section title */}
		<rect x="40" y="5" width="40" height="4" rx="1.5" fill="currentColor" fillOpacity="0.4" />
		{([0, 1, 2] as const).map(i => (
			<g key={i} transform={`translate(${5 + i * 38}, 13)`}>
				<rect width="33" height="38" rx="2.5" fill="currentColor" fillOpacity="0.08" />
				{/* Cover image */}
				<rect x="2" y="2" width="29" height="15" rx="1.5" fill="currentColor" fillOpacity="0.2" />
				{/* Date and tag */}
				<rect x="3" y="20" width="10" height="2" rx="1" fill="currentColor" fillOpacity="0.2" />
				<rect x="15" y="20" width="8" height="2" rx="1" fill="currentColor" fillOpacity="0.3" />
				{/* Title */}
				<rect x="3" y="25" width="24" height="3.5" rx="1" fill="currentColor" fillOpacity="0.4" />
				{/* Excerpt */}
				<rect x="3" y="31" width="27" height="2" rx="1" fill="currentColor" fillOpacity="0.15" />
				<rect x="3" y="34.5" width="20" height="2" rx="1" fill="currentColor" fillOpacity="0.15" />
			</g>
		))}
	</svg>
)

const FooterSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		{/* Page content above footer */}
		<rect x="18" y="5" width="84" height="3.5" rx="1" fill="currentColor" fillOpacity="0.1" />
		<rect x="28" y="12" width="64" height="3.5" rx="1" fill="currentColor" fillOpacity="0.1" />
		{/* Dark footer band */}
		<rect x="0" y="22" width="120" height="34" rx="0" fill="currentColor" fillOpacity="0.22" />
		{/* Column 1 */}
		<rect x="7" y="27" width="20" height="3.5" rx="1" fill="currentColor" fillOpacity="0.55" />
		<rect x="7" y="34" width="16" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		<rect x="7" y="39" width="19" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		<rect x="7" y="44" width="14" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		{/* Column 2 */}
		<rect x="49" y="27" width="20" height="3.5" rx="1" fill="currentColor" fillOpacity="0.55" />
		<rect x="49" y="34" width="16" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		<rect x="49" y="39" width="19" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		<rect x="49" y="44" width="14" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		{/* Column 3 */}
		<rect x="91" y="27" width="20" height="3.5" rx="1" fill="currentColor" fillOpacity="0.55" />
		<rect x="91" y="34" width="16" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
		<rect x="91" y="39" width="19" height="2.5" rx="1" fill="currentColor" fillOpacity="0.3" />
	</svg>
)

const HtmlEmbedSvg: FC<SvgProps> = ({ className }) => (
	<svg viewBox="0 0 120 56" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
		<rect width="120" height="56" rx="3" fill="currentColor" fillOpacity="0.05" />
		<rect x="8" y="7" width="104" height="42" rx="2.5" fill="currentColor" fillOpacity="0.08" />
		<path
			d="M49 20L40 28L49 36M71 20L80 28L71 36M64 17L56 39"
			stroke="currentColor"
			strokeOpacity="0.35"
			strokeWidth="2.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
)

const thumbnailMap: Record<LandingPageBlock["type"], FC<SvgProps>> = {
	header: HeaderSvg,
	hero: HeroSvg,
	text: TextSvg,
	"html-embed": HtmlEmbedSvg,
	"two-columns": TwoColumnsSvg,
	features: FeaturesSvg,
	testimonials: TestimonialsSvg,
	faq: FaqSvg,
	gallery: GallerySvg,
	"lead-form": LeadFormSvg,
	"product-list": ProductListSvg,
	"product-highlight": ProductHighlightSvg,
	"blog-posts": BlogPostsSvg,
	footer: FooterSvg,
}

type Props = { type: LandingPageBlock["type"]; className?: string }
const BlockThumbnail: FC<Props> = ({ type, className }) => {
	const Svg = thumbnailMap[type]
	return <Svg className={className} />
}

export default BlockThumbnail
